// The TruStage claim at fruition (T-174), as the « TruStage claim » section
// shows it. Pure: the state comes from GET preneed-claims/:funeralId (the
// backend's preneed-claims.rules.ts decides; this only words it).

export type ClaimStatus = 'to_prepare' | 'waiting_signature' | 'ready' | 'sent' | 'paid';
export type StepState = 'done' | 'now' | 'wait' | 'pending';
export type ClaimAction = 'review' | 'resend' | 'send' | 'mark_paid';

export interface ClaimRow {
  region: string;
  language: string;
  policy_number: string | null;
  cleo_signer: 'bram' | 'megan' | null;
  approved_by_name: string | null;
  approved_at: string | null;
  form_sent_at: string | null;
  form_signed_at: string | null;
  sent_at: string | null;
  sent_to: string | null;
  send_error: string | null;
  paid_at: string | null;
  paid_amount: number | null;
}

export interface ClaimState {
  claimable: boolean;
  enabled: boolean;
  ontarioEnabled: boolean;
  region: 'quebec' | 'ontario';
  language: 'en' | 'fr';
  contractId: number | null;
  fulfilledAt: string | null;
  claim: ClaimRow | null;
  status: ClaimStatus;
  cremated: boolean;
  pod: { id: string; verificationId: string } | null;
  policyNumber: string;
  policyIsApplication: boolean;
  hasCertificate: boolean;
  defaultCleoSigner: 'bram' | 'megan';
  signer: { id: number; name: string; email: string | null } | null;
  sendBlocker: string | null;
}

export interface ClaimStep {
  key: string;
  label: string;
  state: StepState;
  pill: string;
  note?: string;
  actions?: ClaimAction[];
}

export const NOT_PAID_DAYS = 30;

const DAY = 24 * 60 * 60 * 1000;
export function daysSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.floor((now.getTime() - t) / DAY);
}

const short = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const money = (n: number | null | undefined) =>
  n == null ? '' : `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** One status word for the section header and lists. */
export function claimStatusWord(state: Pick<ClaimState, 'status' | 'claim'>, now = new Date()): string {
  switch (state.status) {
    case 'to_prepare':
      return 'To prepare';
    case 'waiting_signature':
      return 'Waiting for signature';
    case 'ready':
      return 'Ready to send';
    case 'sent': {
      const d = daysSince(state.claim?.sent_at, now);
      return d == null ? 'Claim sent' : `Claim sent · ${d}d`;
    }
    case 'paid':
      return 'Claim paid';
  }
}

export function claimStatusTone(state: Pick<ClaimState, 'status' | 'claim'>, now = new Date()): 'ok' | 'warn' | 'info' {
  if (state.status === 'paid') return 'ok';
  if (state.status === 'sent') return (daysSince(state.claim?.sent_at, now) ?? 0) >= NOT_PAID_DAYS ? 'warn' : 'info';
  return 'warn';
}

const BLOCKER_NOTE: Record<string, string> = {
  disabled: 'TruStage claims are switched off (preneed_claims_enabled). Nothing is sent.',
  ontario_off: 'Ontario claims are off until TruStage confirms the process.',
  not_cremated: 'Sends automatically once the cremation is done.',
  no_pod: 'Issue the proof of death first: it goes with the claim.',
};

export function claimSteps(state: ClaimState, now = new Date()): ClaimStep[] {
  const c = state.claim;
  const quebec = state.region !== 'ontario';
  const steps: ClaimStep[] = [];

  steps.push({
    key: 'policy',
    label: 'Policy',
    state: state.policyNumber && !state.policyIsApplication ? 'done' : 'wait',
    pill: state.policyNumber ? (state.policyIsApplication ? 'Application #' : state.policyNumber) : 'Missing',
    note: !state.policyNumber
      ? 'No certificate or application number on file: type the policy # in the form.'
      : state.policyIsApplication
        ? `No TruStage certificate yet: the form uses ${state.policyNumber}.`
        : undefined,
  });

  steps.push({
    key: 'approved',
    label: 'Claim form',
    state: c?.approved_at ? 'done' : 'now',
    pill: c?.approved_at ? `Approved ${short(c.approved_at)}` : 'To review',
    note: c?.approved_at
      ? `Approved by ${c.approved_by_name ?? 'staff'}; ${c.cleo_signer === 'megan' ? 'Megan' : 'Bram'}'s signature in Section B.`
      : state.enabled
        ? 'Review the prefilled form, then approve: the stored signature goes in Section B.'
        : BLOCKER_NOTE.disabled,
    actions: c?.sent_at ? undefined : ['review'],
  });

  if (quebec) {
    const waiting = !!c?.form_sent_at && !c?.form_signed_at;
    steps.push({
      key: 'family',
      label: 'Family signature',
      state: c?.form_signed_at ? 'done' : waiting ? ((daysSince(c?.form_sent_at, now) ?? 0) >= 3 ? 'wait' : 'now') : 'pending',
      pill: c?.form_signed_at ? `Signed ${short(c.form_signed_at)}` : waiting ? `Sent ${short(c?.form_sent_at)}` : 'After approval',
      note: waiting ? `Section C, sent to ${state.signer?.name ?? 'the family'}${state.signer?.email ? ` (${state.signer.email})` : ''}.` : undefined,
      actions: waiting && state.enabled ? ['resend'] : undefined,
    });
  }

  steps.push({
    key: 'pod',
    label: 'Proof of death',
    state: state.pod ? 'done' : 'wait',
    pill: state.pod ? state.pod.verificationId : 'Not issued',
  });

  const blocker = state.sendBlocker;
  const sendable = state.status === 'ready' && !blocker;
  steps.push({
    key: 'sent',
    label: 'Claim sent to TruStage',
    state: c?.sent_at ? 'done' : state.status === 'ready' ? (blocker ? 'wait' : 'now') : 'pending',
    pill: c?.sent_at ? `Sent ${short(c.sent_at)}` : state.status === 'ready' ? (blocker === 'not_cremated' ? 'After cremation' : 'Ready') : 'Pending',
    note: c?.sent_at
      ? `To ${c.sent_to}.`
      : c?.send_error
        ? `Last attempt failed: ${c.send_error}`
        : state.status === 'ready' && blocker
          ? BLOCKER_NOTE[blocker]
          : undefined,
    actions: sendable ? ['send'] : undefined,
  });

  const unpaidDays = daysSince(c?.sent_at, now);
  steps.push({
    key: 'paid',
    label: 'Claim paid',
    state: c?.paid_at ? 'done' : c?.sent_at ? ((unpaidDays ?? 0) >= NOT_PAID_DAYS ? 'wait' : 'now') : 'pending',
    pill: c?.paid_at ? `${money(c.paid_amount)} · ${short(c.paid_at)}` : c?.sent_at ? `Waiting ${unpaidDays}d` : 'Pending',
    actions: c?.sent_at && !c?.paid_at ? ['mark_paid'] : undefined,
  });

  return steps;
}
